import { useState, useRef, useEffect } from 'react';
import { Share2, Link2 } from 'lucide-react';
import { api } from '../../api/client';

interface Props {
  startup: Record<string, any>;
  currentDay: number;
  signalCount: number;
  verdict?: string;
}

const C = {
  surface: '#161627',
  border: '#2a2a3e',
  primary: '#7c6ff7',
  text: '#f0f0ff',
  text2: '#8888aa',
  green: '#22c55e',
};

export default function ShareProgressButton({ startup, currentDay, signalCount, verdict }: Props) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);

  const hasMvp = startup.mvpBlueprint?.status === 'complete';
  const name = startup.name || 'my startup idea';

  const verdictText = verdict ? ` Verdict: ${verdict}.` : '';
  const shareText = hasMvp
    ? `Just built my MVP Blueprint after validating my startup idea in 14 days 🚀${verdictText} Built with @StartupScoreLive #buildinpublic`
    : `Day ${currentDay} of validating ${name} 🚀 — ${signalCount} market signal${signalCount !== 1 ? 's' : ''} logged.${verdictText} Building in public. #buildinpublic #startups`;

  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent('https://startupscore.live')}`;

  const trackShare = () => {
    api.post(`/startups/${startup.id}/track-share`).catch(() => {});
  };

  // Close popover on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div style={{ position: 'relative' }} ref={popRef}>
      <button onClick={() => setOpen(!open)} style={{
        background: 'transparent', border: `1px solid ${C.border}`,
        borderRadius: 8, color: C.text2, fontSize: 12, fontWeight: 600,
        padding: '6px 12px', cursor: 'pointer',
      }}>
        <Share2 size={13} /> Share Progress
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 6,
          background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 10, padding: 12, zIndex: 50, minWidth: 180,
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        }}>
          <a href={twitterUrl} target="_blank" rel="noopener noreferrer"
            onClick={() => { trackShare(); setOpen(false); }}
            style={{
              display: 'block', padding: '8px 10px', borderRadius: 6,
              color: '#1DA1F2', fontSize: 12, fontWeight: 600, textDecoration: 'none',
              marginBottom: 4,
            }}>
            Share on X
          </a>
          <a href={linkedinUrl} target="_blank" rel="noopener noreferrer"
            onClick={() => { trackShare(); setOpen(false); }}
            style={{
              display: 'block', padding: '8px 10px', borderRadius: 6,
              color: '#0A66C2', fontSize: 12, fontWeight: 600, textDecoration: 'none',
              marginBottom: 4,
            }}>
            Share on LinkedIn
          </a>
          {/* FEATURE 2: Copy shareable report URL */}
          <button onClick={() => {
            const reportUrl = `${window.location.origin}/report/${startup.id || startup._id}`;
            navigator.clipboard.writeText(reportUrl);
            setCopied(true); setTimeout(() => setCopied(false), 2000);
            trackShare(); setOpen(false);
          }} style={{
            display: 'block', width: '100%', textAlign: 'left',
            padding: '8px 10px', borderRadius: 6, background: 'transparent',
            border: 'none', color: C.green, fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}>
            {copied ? 'Link copied!' : <><Link2 size={12} /> Copy report link</>}
          </button>
          <button onClick={() => {
            navigator.clipboard.writeText(shareText);
            setCopied(true); setTimeout(() => setCopied(false), 2000);
            trackShare(); setOpen(false);
          }} style={{
            display: 'block', width: '100%', textAlign: 'left',
            padding: '8px 10px', borderRadius: 6, background: 'transparent',
            border: 'none', color: C.text2, fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}>
            {copied ? 'Copied!' : 'Copy text'}
          </button>
        </div>
      )}
    </div>
  );
}
